import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import type { SecretListing } from "@repo/app-types/entities/secret";
import {
  codeToolMaxTimeoutMs,
  defaultCodeToolTimeoutMs,
} from "@repo/app-types/entities/tools/code";
import { buildCodeToolScriptApi } from "@repo/app-types/entities/tools/codeToolScriptApi";
import type { CustomToolParameter } from "@repo/app-types/entities/tools/custom";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { Input } from "@repo/ui/Inputs/input";
import { useMemo, type FC } from "react";
import { useAllUserCustomFieldDefinitionsQuery } from "../../../api/entities/userCustomField.js";
import type { CodeToolTestInputFormSettings } from "../formConfigs/codeToolTestInput.js";
import type { ToolMockFormSettings } from "../formConfigs/toolMockResponse.js";
import { applyAllowedDomainsChange } from "../formConfigs/webDocumentResearchAllowedDomains.js";
import type { BaseToolFormData } from "../hooks/useBaseToolForm.js";
import {
  CodeToolCodeField,
  type CodeToolErrorPosition,
} from "./codeToolCodeField.js";
import { CodeToolSecretsEditor } from "./codeToolSecretsEditor.js";
import { CustomToolParameterFormBuilder } from "./customToolParameterFormBuilder.js";
import { EndsCallField } from "./endsCallField.js";
import { SharedToolFormFields } from "./sharedToolFormFields.js";
import { SigningKeyDisplay } from "./signingKeyDisplay.js";
import { ToolMockResponseField } from "./toolMockResponseField.js";

interface CodeToolFormProps {
  name: string;
  description: string;
  settings: CodeToolFormSettings;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSettingsChange: (settings: Partial<CodeToolFormSettings>) => void;
  isEditMode: boolean;
  /** Set while a save is in flight: the editors take no edits. */
  isReadOnly: boolean;
  /** Shows the fields' changes. When set, their toggles don't render. */
  isShowingChanges?: boolean;
  /** The saved tool, to diff against; `undefined` for a new tool. */
  original: BaseToolFormData<CodeToolFormSettings> | undefined;
}

/** The test input is edited in the test panel beside the form. */
export interface CodeToolFormSettings
  extends ToolMockFormSettings, CodeToolTestInputFormSettings {
  code: string;
  codeError: string | undefined;
  /** Where the saved code fails to compile, when the API says. */
  codeErrorPosition: CodeToolErrorPosition | undefined;
  parameters: readonly CustomToolParameter[];
  allowedDomains: readonly string[];
  allowedDomainsError: string | undefined;
  timeout: string;
  timeoutError: string | undefined;
  endsCall: boolean;
  /** The secrets the tool references, saved or not. */
  secrets: readonly SecretListing[];
  /** The secrets referenced when the form loaded, to tell whether the form changed them. */
  storedSecrets: readonly SecretListing[];
}

export const CodeToolForm: FC<CodeToolFormProps> = (props) => {
  const {
    name,
    description,
    settings,
    onNameChange,
    onDescriptionChange,
    onSettingsChange,
    isEditMode,
    isReadOnly,
    isShowingChanges,
    original,
  } = props;

  const { data: customFieldDefinitions } =
    useAllUserCustomFieldDefinitionsQuery();
  // From the form's unsaved state, so a parameter or secret just added
  // completes straight away.
  const scriptApi = useMemo(
    () =>
      buildCodeToolScriptApi({
        parameters: settings.parameters.filter(({ name }) => name !== ""),
        contactCustomFields: (customFieldDefinitions ?? []).filter(
          ({ resource_type }) =>
            resource_type === CustomFieldResourceType.CONTACT,
        ),
        interactionCustomFields: (customFieldDefinitions ?? []).filter(
          ({ resource_type }) =>
            resource_type === CustomFieldResourceType.GRADING,
        ),
        secretNames: settings.secrets
          .map(({ name }) => name)
          .filter((name) => name !== ""),
      }),
    [settings.parameters, settings.secrets, customFieldDefinitions],
  );

  return (
    <>
      {isEditMode && <SigningKeyDisplay />}

      <SharedToolFormFields
        name={name}
        description={description}
        onNameChange={onNameChange}
        originalName={original?.name}
        originalDescription={original?.description}
        onDescriptionChange={onDescriptionChange}
        isReadOnly={isReadOnly}
        isShowingChanges={isShowingChanges}
        namePlaceholder="Look up the next delivery"
        descriptionPlaceholder="This tool finds the caller's next delivery and when it is due. The agent should use this if the caller asks about an order."
      />

      <CustomToolParameterFormBuilder
        label="Parameters"
        description="Define the parameters the agent should provide. The script reads them as input.params."
        value={settings.parameters}
        onChange={(parameters) => onSettingsChange({ parameters })}
      />

      <CodeToolCodeField
        isReadOnly={isReadOnly}
        isShowingChanges={isShowingChanges}
        value={settings.code}
        originalValue={original?.settings.code}
        onChange={(code) =>
          onSettingsChange({
            code,
            codeError: undefined,
            codeErrorPosition: undefined,
          })
        }
        errorMessage={settings.codeError}
        errorPosition={settings.codeErrorPosition}
        scriptApi={scriptApi}
      />

      <CreatableComboboxField
        label="Allowed domains"
        placeholder="Add a domain (e.g. api.example.com)"
        value={settings.allowedDomains}
        onChange={(allowedDomains) =>
          onSettingsChange(
            applyAllowedDomainsChange(settings.allowedDomains, allowedDomains),
          )
        }
        description="The script can only send requests to these domains. Enter exact hostnames without https://, a path, port, or wildcard. Subdomains must be added separately."
        errorMessage={settings.allowedDomainsError}
      />

      <CodeToolSecretsEditor
        secrets={settings.secrets}
        toolDomains={settings.allowedDomains}
        // A save refused for a secret the tool can't send shows its error on
        // the domains field, so changing the secrets clears it too.
        onChange={(secrets) =>
          onSettingsChange({ secrets, allowedDomainsError: undefined })
        }
      />

      <Input
        id="codeToolTimeout"
        label="Timeout (ms)"
        type="number"
        min={1}
        max={codeToolMaxTimeoutMs}
        value={settings.timeout}
        onChange={(e) =>
          onSettingsChange({ timeout: e.target.value, timeoutError: undefined })
        }
        placeholder={defaultCodeToolTimeoutMs.toString()}
        description={`How long a run may take, at most ${codeToolMaxTimeoutMs.toLocaleString()} ms.`}
        errorDescription={settings.timeoutError}
      />

      <EndsCallField
        isChecked={settings.endsCall}
        onChange={(endsCall) => onSettingsChange({ endsCall })}
      />

      <ToolMockResponseField
        settings={settings}
        onSettingsChange={onSettingsChange}
        isReadOnly={isReadOnly}
      />
    </>
  );
};
